import { useCallback, useEffect, useRef, useState } from "react";
import { heroReel, films, stills } from "@/config/media";
import useIosSafeVideo from "@/lib/useIosSafeVideo";

// Three-clip reel: two stacked video layers, opacity crossfade on end, the
// back layer preloads the next clip while the front plays.
export default function HeroReel({ paused = false, onClipChange, onFirstPlay }) {
  const [front, setFront] = useState(0);
  const [live, setLive] = useState(false);
  const [layerClips, setLayerClips] = useState([0, 1]);
  const refA = useRef(null);
  const refB = useRef(null);
  const refs = [refA, refB];
  useIosSafeVideo(refA);
  useIosSafeVideo(refB);
  const activeClip = layerClips[front];

  const go = useCallback(
    (next) => {
      const back = 1 - front;
      setLayerClips((lc) => {
        if (lc[back] === next) return lc;
        const c = [...lc];
        c[back] = next;
        return c;
      });
      requestAnimationFrame(() => {
        const v = refs[back].current;
        if (v) {
          v.currentTime = 0;
          v.play().catch(() => {});
        }
        setFront(back);
        onClipChange?.(next);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [front, onClipChange],
  );

  // Belt-and-braces for iOS Safari: mirror muted onto the HTML attribute
  // — moved to the shared useIosSafeVideo hook above so every <video> in
  // the app follows the same rule.

  // viewport / intro gate
  useEffect(() => {
    const v = refs[front].current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [front, paused]);

  // after the crossfade settles, point the back layer at the next clip
  useEffect(() => {
    const next = (activeClip + 1) % heroReel.length;
    const back = 1 - front;
    const t = setTimeout(() => {
      setLayerClips((lc) => {
        if (lc[back] === next) return lc;
        const c = [...lc];
        c[back] = next;
        return c;
      });
    }, 1100);
    return () => clearTimeout(t);
  }, [activeClip, front]);

  return (
    <>
      {[0, 1].map((i) => {
        const clip = heroReel[layerClips[i]];
        // Only clip one fetches before first play; the back layer's src
        // attaches after the front clip reports canplay.
        const fetchAllowed = i === 0 || live;
        return (
          <video
            key={i}
            ref={refs[i]}
            src={fetchAllowed ? films[clip.film] : undefined}
            muted
            autoPlay
            playsInline
            preload="auto"
            onCanPlay={() => {
              if (i === 0) {
                setLive(true);
                onFirstPlay?.();
              }
            }}
            onEnded={() => i === front && go((activeClip + 1) % heroReel.length)}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out"
            style={{ opacity: live && front === i ? 1 : 0 }}
            aria-hidden="true"
            data-testid={`hero-reel-layer-${i}`}
          />
        );
      })}
      <div
        className="absolute bottom-24 right-6 z-10 flex flex-col gap-2 sm:right-10"
        data-testid="hero-chapters"
      >
        {heroReel.map((c, i) => (
          <button
            key={c.film}
            onClick={() => i !== activeClip && go(i)}
            aria-label={`Play ${c.title}`}
            data-testid={`hero-chapter-${i}`}
            className="group flex items-center justify-end gap-2 p-1"
          >
            <span className="font-mono text-[9px] tracking-[0.25em] text-mutedText opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {c.title.toUpperCase()}
            </span>
            <span
              className={`block h-1.5 w-1.5 rounded-full border border-gold transition-all duration-500 ${
                activeClip === i ? "scale-125 bg-gold" : "bg-transparent"
              }`}
            />
          </button>
        ))}
      </div>
    </>
  );
}
